import axiosInstance from '../config/axiosApi/axiosApi';
import { AUTH_CONFIG } from '../config/authConfig';

/**
 * Authentication Service
 * Handles all authentication-related operations
 */
class AuthService {
  constructor() {
    this.logoutPromise = null;
  }

  clearClientSession() {
    localStorage.removeItem('user');
    localStorage.removeItem('keepMeLogin');
    localStorage.removeItem('itemGridPreferences');
    sessionStorage.clear();

    // Best effort cleanup for JS-readable cookies.
    document.cookie = "csrf_token=; Max-Age=0; path=/";
  }

  /**
   * Login user with credentials
   * @param {string} userName - Username
   * @param {string} password - Password
   * @returns {Promise<Object>} User data and tokens
   */
  async login(userName, password) {
    try {
      const response = await axiosInstance.post(AUTH_CONFIG.ENDPOINTS.LOGIN, {
        userName,
        password,
        keepMeLogin: true
      });

      if (response.status === 200 && response.data.status) {
        const { userData } = response.data.data;

        return {
          success: true,
          data: { userData }
        };
      }

      return {
        success: false,
        error: response.data.message || 'Login failed'
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Login failed'
      };
    }
  }

  /**
   * Refresh access token using refresh token
   * @returns {Promise<Object>} New tokens
   */
  async refreshToken() {
    try {
      const response = await axiosInstance.post(AUTH_CONFIG.ENDPOINTS.REFRESH, {
        clientType: 'web'
      });

      // validateStatus accepts 401 as a resolved response — treat as failure.
      if (response?.status === 401 || !response?.data?.status) {
        return {
          success: false,
          error: response?.data?.message || 'Token refresh failed'
        };
      }

      return {
        success: true,
        data: response.data.data || {}
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Token refresh failed'
      };
    }
  }

  /**
   * Logout user and clear all tokens
   * @returns {Promise<Object>} Logout result
   */
  async logout(options = {}) {
    const {
      revokeServerSession = true
    } = options;

    if (this.logoutPromise) {
      return this.logoutPromise;
    }

    this.logoutPromise = (async () => {
      try {
        if (revokeServerSession && AUTH_CONFIG.ENDPOINTS.LOGOUT) {
          await axiosInstance.post(AUTH_CONFIG.ENDPOINTS.LOGOUT);
        }

        return { success: true };
      } catch (error) {
        return {
          success: false,
          error: error?.response?.data?.message || "Logout request failed"
        };
      } finally {
        this.clearClientSession();
      }
    })();

    try {
      return await this.logoutPromise;
    } finally {
      this.logoutPromise = null;
    }
  }

  /**
   * Validate current token
   * @returns {Promise<Object>} Validation result
   */
  async validateToken() {
    try {
      const response = await axiosInstance.get(AUTH_CONFIG.ENDPOINTS.VALIDATE);
      return {
        success: true,
        valid: response.data.status,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        valid: false,
        error: error.response?.data?.message || 'Token validation failed'
      };
    }
  }

  /**
   * Get current user data
   * @returns {Object|null} User data
   */
  getCurrentUser() {
    if (AUTH_CONFIG.FEATURES.HTTP_ONLY_COOKIES) {
      // When using httpOnly cookies, user data should come from API
      return null;
    } else {
      try {
        const userData = localStorage.getItem('user');
        return userData ? JSON.parse(userData) : null;
      } catch (error) {
        return null;
      }
    }
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} Authentication status
   */
  isAuthenticated() {
    const user = this.getCurrentUser();
    return !!(user && user.userData);
  }

  /**
   * Get access token
   * @returns {string|null} Access token
   */
  getAccessToken() {
    return null;
  }

  /**
   * Get refresh token
   * @returns {string|null} Refresh token
   */
  getRefreshToken() {
    return null;
  }
}

export default new AuthService();
